/**
 * Etapes — un parcours en étapes numérotées : où l'on en est, ce qui est fait,
 * ce qui reste.
 *
 *   <Etapes etapes={[{ cle: "identite", etiquette: "Identité" }, …]} courante={1} />
 *   <Etapes etapes={ETAPES} courante={vue} faites={["identite", "telegram"]} onAller={setVue} />
 *   <Etapes sens="vertical" etapes={…} courante={2} />          ← une liste d'instructions, un tiroir
 *
 * L'étape en cours porte la marque (c'est la seule chose ACTIVE) ; une étape
 * faite, l'encre pleine et une coche ; une étape à venir, son numéro en creux ;
 * une étape en erreur, le rouge de sens. Sans `faites`, tout ce qui précède la
 * courante est fait. `onAller` rend les étapes faites cliquables (revenir en
 * arrière dans un assistant) ; `libre` les rend toutes cliquables.
 *
 * Sur un téléphone (le conteneur sous 460 px), la rangée horizontale ne garde
 * que les pastilles et écrit l'étape en cours dessous — « Étape 2 sur 4 ·
 * Telegram » — au lieu de tronquer quatre noms illisibles.
 *
 * Successeur des deux `Stepper` du cockpit (`core/auth/onboarding/OnboardingChrome`,
 * `shell/creator-onboarding/WizardChrome`) et des étapes numérotées d'un état
 * vide (`creatorTabs`).
 */
import type { ReactNode } from "react";

import { Button } from "../Button";
import { Check, X } from "../icons";
import { cn } from "../utils";
import "./saisie.css";

export interface Etape {
  cle: string;
  etiquette: ReactNode;
  /** Sous le nom, en gris (en `vertical` surtout). */
  aide?: ReactNode;
  /** L'étape a échoué : le rouge de sens, une croix. */
  erreur?: boolean;
}

export type EtatEtape = "fait" | "courante" | "avenir" | "erreur";

export interface EtapesProps {
  etapes: readonly Etape[];
  /** L'index de l'étape en cours (0 pour la première). */
  courante: number;
  /** Les clés des étapes faites, quand elles ne sont pas simplement « celles d'avant » (un assistant qu'on parcourt librement). */
  faites?: readonly string[];
  /** Aller à une étape (son index) : les étapes faites deviennent des boutons. */
  onAller?: (index: number) => void;
  /** Toutes les étapes sont cliquables, faites ou non. */
  libre?: boolean;
  sens?: "horizontal" | "vertical";
  /** Le résumé sous les pastilles sur un téléphone — déjà traduit (défaut « Étape 2 sur 4 »). */
  resume?: (numero: number, total: number) => ReactNode;
  /** Le nom du parcours pour un lecteur d'écran. */
  ariaLabel?: string;
  className?: string;
}

export function etatDe(index: number, etape: Etape, courante: number, faites?: readonly string[]): EtatEtape {
  if (etape.erreur) return "erreur";
  if (index === courante) return "courante";
  if (faites ? faites.includes(etape.cle) : index < courante) return "fait";
  return "avenir";
}

export function Etapes({
  etapes,
  courante,
  faites,
  onAller,
  libre = false,
  sens = "horizontal",
  resume = (n, total) => `Étape ${n} sur ${total}`,
  ariaLabel,
  className,
}: EtapesProps) {
  const enCours = etapes[courante];
  return (
    <nav className={cn("si-etapes", className)} data-sens={sens} aria-label={ariaLabel}>
      <ol className="si-etapes-liste">
        {etapes.map((e, i) => {
          const etat = etatDe(i, e, courante, faites);
          const suivante = etapes[i + 1];
          const traitFait = suivante !== undefined && etat === "fait" && etatDe(i + 1, suivante, courante, faites) !== "avenir";
          const cliquable = !!onAller && i !== courante && (libre || etat === "fait" || etat === "erreur");
          const contenuPastille =
            etat === "fait" ? <Check weight="bold" aria-hidden /> : etat === "erreur" ? <X weight="bold" aria-hidden /> : i + 1;
          return (
            <li key={e.cle} className="si-etape" data-etat={etat} aria-current={etat === "courante" ? "step" : undefined}>
              {suivante !== undefined && <span className="si-etape-trait" data-plein={traitFait || undefined} aria-hidden />}
              {cliquable ? (
                <Button
                  variant="ghost"
                  size="lg"
                  iconOnly
                  className="si-etape-bouton"
                  onClick={() => onAller!(i)}
                  aria-label={typeof e.etiquette === "string" ? `${i + 1}. ${e.etiquette}` : undefined}
                >
                  <span className="si-etape-pastille si-rond">{contenuPastille}</span>
                </Button>
              ) : (
                <span className="si-etape-marque" aria-hidden>
                  <span className="si-etape-pastille si-rond">{contenuPastille}</span>
                </span>
              )}
              <span className="si-etape-textes">
                <span className="si-etape-nom">{e.etiquette}</span>
                {e.aide !== undefined && <span className="si-etape-aide">{e.aide}</span>}
              </span>
            </li>
          );
        })}
      </ol>
      {sens === "horizontal" && enCours && (
        <p className="si-etapes-resume" aria-hidden>
          {resume(courante + 1, etapes.length)} · <b>{enCours.etiquette}</b>
        </p>
      )}
    </nav>
  );
}
